import type { WireJobHealth, WireStanding } from '@/api/wire';
import { denialFor } from '@/api/denials';

/**
 * Where a request or a task stands, in words (ADR-0169, ADR-0140). Pure: the
 * standing the server composed from the platform's own rows, and how to name
 * a tool. The codes stay on the wire; a person reads what they mean, and a
 * superadmin also reads the platform's sentences as recorded.
 */

export interface StandingWords {
  /** The one line that says whether it is moving. */
  headline: string;
  /** What was found, each a sentence a person reads. */
  lines: string[];
  /** Who can move it, when something can. */
  who: string | null;
}

const time = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) + ' UTC';

export function standingWords(s: WireStanding, toolName: (id: string) => string): StandingWords {
  const f = s.facts;
  const lines: string[] = [];
  let who: string | null = null;
  if (f.departmentStopped) {
    lines.push('A person stopped this department’s agents, so nothing of it runs.');
    who = 'Superadmin';
  }
  if (f.agent) lines.push(f.agentStopped ? `${f.agent} has it, and ${f.agent} is stopped.` : `${f.agent} has it.`);
  const run = f.run;
  if (run) {
    switch (run.state) {
      case 'queued':
        lines.push(run.waitsUntil ? `Its work waits until ${time(run.waitsUntil)} to try again.` : 'Its work is queued and will start within a minute or two.');
        break;
      case 'running':
        lines.push('Its work is running now.');
        break;
      case 'failed':
        lines.push(`Its work failed on try ${run.attempts} of ${run.maxAttempts}, and will be tried again.`);
        break;
      case 'dead':
        lines.push(`Its work gave up after ${run.attempts} tries, so it goes back to a person.`);
        break;
      case 'done':
        lines.push('Its last piece of work finished.');
        break;
      default:
        break;
    }
  } else if (f.agent) {
    lines.push('Its work has not started yet.');
  }
  for (const r of f.refusals.slice(0, 3)) {
    const d = denialFor(r.rule);
    lines.push(`${d.title}: ${toolName(r.tool)}, ${r.count === 1 ? 'once' : `${r.count} times`}.`);
    who ??= d.who;
  }
  if (f.waitingOn.length > 0) {
    lines.push(`It waits for ${f.waitingOn.map(toolName).join(', ')} to be granted to it.`);
    who ??= 'Superadmin';
  }
  const stuck = s.blockedBy !== null;
  return {
    headline: stuck ? 'It is not moving.' : run?.state === 'running' || run?.state === 'queued' ? 'It is moving.' : 'Nothing is holding it up that the platform can see.',
    lines,
    who: stuck ? who : null,
  };
}

/** A kind of background job, in words. The code stays for the superadmin's "as recorded". */
const JOB_WORDS: Record<string, string> = {
  run_task: 'Agents’ tasks',
  sync_calendar: 'Calendar sync',
  sync_mailbox: 'Shared inbox sync',
  sync_own_mailbox: 'Personal inbox sync',
  prepare_brief: 'Meeting briefs',
  draft_reply: 'Reply drafts',
  notify_blocker: 'Notices to the superadmin',
  embed_index: 'The index by meaning',
  compile_spec: 'Writing up changes',
  sweep_plan: 'Prospecting sweeps',
  read_site: 'Reading websites',
  meeting_turn: 'Project meetings',
  meeting_minutes: 'Meeting minutes',
};

export function jobWords(kind: string): string {
  return JOB_WORDS[kind] ?? kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function jobLine(j: WireJobHealth): string {
  const parts = [`${j.done} finished`, j.failing ? `${j.failing} failing` : null, j.dead ? `${j.dead} gave up` : null].filter(Boolean);
  // All of it on a build a deploy has since replaced: history, said as such (0167).
  const earlier = (j.onEarlierBuild ?? 0) > 0 && (j.onEarlierBuild ?? 0) >= j.dead + j.failing ? ' All of it before the latest deploy; nothing has failed since.' : '';
  return `${jobWords(j.kind)}: ${parts.join(', ')}.${earlier}`;
}
